import { ParsedBalloonQRCode, parseBalloonQrCode } from '@allurion/domain';
import { CameraOnIcon, IconButton } from '@allurion/ui';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { toastError } from 'src/components/ui/toasts';

import { QRCodeReader } from './QRCodeReader';

import styles from './ScanBalloonQRCode.module.scss';

type Props = {
  onValid: (result: ParsedBalloonQRCode) => Promise<void>;
};

export function ScanBalloonQRCode({ onValid }: Props) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerId = 'reader';
  const [cameras, setCameras] = useState(-1);

  const startCapturing = useCallback(async () => {
    try {
      await QRCodeReader.init(containerId);

      const cameras = await QRCodeReader.countCamerasAvailable();

      setCameras(cameras);

      await QRCodeReader.start({ parse: parseBalloonQrCode, onValid });
    } catch (error) {
      captureException(error);
      toastError(
        intl.formatMessage({
          id: 'balloonPlacement.scanQRCode.error',
          defaultMessage: 'Error starting QR code reader. Try again.',
        })
      );
    }
  }, [intl, onValid]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const timeout = setTimeout(startCapturing, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [startCapturing]);

  const needsSwitchCameraBtn = cameras > 1;

  return (
    <div className={styles.container}>
      {cameras === 0 ? (
        <div className={styles.noCamera}>
          <p>
            {intl.formatMessage({
              id: 'balloonPlacement.scanQRCode.noCamera',
              defaultMessage: 'No camera available',
            })}
          </p>
        </div>
      ) : (
        <>
          {needsSwitchCameraBtn && (
            <IconButton
              onClick={switchCamera}
              icon={<CameraOnIcon />}
              className={styles.switchCamera}
            />
          )}
          <div ref={containerRef} id={containerId}></div>
        </>
      )}
    </div>
  );

  async function switchCamera() {
    //FIXME:disable toggle button during camera switching
    QRCodeReader.switchCameraFacingMode();
    await startCapturing();
  }
}
